import { Component, OnInit } from '@angular/core';
import * as Rx from 'rxjs';
import { FormBuilder, FormGroup, FormControl, Validators, FormArray} from '@angular/forms';
import { FirestoreAccess } from "src/lib/firestore-access";
import { MagliveApiClient } from "src/lib/api-client/maglive-api-client";
import {
  Live,
  LiveThumbnail,
  LiveUpdateRequest,
  GlobalNgWord,
  MagazineNgWord
} from "src/lib/entity/live";
import { Room, Rtmp } from "src/lib/entity/room";
import { BROADCAST_TYPE } from "src/lib/constants";
import { validateCurrentTime30PartAfterThanSmall, validateTooManyValues } from "src/app/parts/validation/admin/LiveScheduledAtValidator.directive"
import { environment } from "src/env/environment";
import { map, concatMap, mergeMap, catchError, tap, throttleTime } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { containNgWordValidator } from 'src/app/parts/validation/admin/ngWordValidator.directive';
import { MagliveUser } from 'src/lib/entity/user';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
  selector: 'admin-live-edit',
  templateUrl: './live-edit.component.html',
  styleUrls: ['../../../../pages/admin/startbootstrap-sb-admin-gh-pages/src/scss/styles.scss', './live-edit.component.scss']
})
export class LiveEditComponent implements OnInit {

  ticketUpdateUrl: string;
  defaultThumbnailUrl: string;
  loginCallbackURL = environment.baseURL.mag2mypage + 'Login.do?ref=';

  // フォーム関係
  formGroup: FormGroup;
  selectedThumbnailFile: File;
  invalidThumbnailSize: boolean;
  isWaiting: boolean;
  allNgWordList: string[];
  globalNgWords$: Rx.Observable<GlobalNgWord[] | "NOT_AUTHORIZED">;

  // 配信関系
  liveId: string;
  live: Live;
  room: Room;
  rtmp: Rtmp;
  creator: MagliveUser;
  liveCreatorComment: string;
  isTicketLive = false;
  private isDuringReservation: boolean;

  constructor(
    private fb: FormBuilder,
    private fsa: FirestoreAccess,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.route.params.subscribe(params =>
      this.liveId = params['id'],
    );
  }

  ngOnInit(): void {
    if (!this.liveId) {
      this.cancelEditLive();
    } else {
      if (environment.name === "local") {
        this.defaultThumbnailUrl = 'https://live.stg.mag2.com/assets/images/thumbnail.png';
      } else {
        this.defaultThumbnailUrl = environment.baseURL.magliveWeb + 'assets/images/thumbnail.png';
      }

      this.createForm();

      this.fsa.getCreatorComment(this.liveId).subscribe((x) => {
        this.liveCreatorComment = x.length > 0 ? x[0].message : "";
        this.formGroup.patchValue({
          creatorComment: this.liveCreatorComment
        });
      });

      this.fsa.getLive(this.liveId).pipe(
        map(x => {
          if (x != null) {
            this.live = x;
            this.isTicketLive = x.options.viewerSegment === 'TICKET';
            if (this.isTicketLive) {
              this.ticketUpdateUrl = environment.baseURL.magticketWeb + `owner/ticket/update/${x.ticketEventId}/?action=close`;
              this.loginCallbackURL = this.loginCallbackURL + encodeURIComponent(this.ticketUpdateUrl);
            }
            this.getCreatorByUserId(x.userId);
            this.getNgWord(x.magazineId);
          }
          return x;
        }),
        mergeMap(x => {
          return this.fsa.getRoom(x.roomId).pipe(
            map(room => {
              this.room = room;
              this.isDuringReservation = x.status === 'RESERVED' && x.scheduledAt !== null && room.status === 'booking' && room.kind === 'scheduled';
            })
          )
        }),
        mergeMap(() => {
          return MagliveApiClient.getRtmpInfo(this.live.id).pipe(
            map(x => {
              if (x === "NOT_AUTHORIZED" || x === "NOT_FOUND") {
                this.toastr.error("配信サーバーの情報取得に失敗しました");
                return Rx.of();
              }
              this.rtmp = x;
            }),
            catchError(() => {
              this.toastr.error("配信サーバーの情報取得に失敗しました");
              return Rx.EMPTY;
            })
          )
        })
      ).subscribe(() => {
        this.configureFormGroup();
      });
    }
  }

  get title() { return this.formGroup.get('title'); }
  get description() { return this.formGroup.get('description'); }
  get broadcastType() { return this.formGroup.get('broadcastType'); }
  get scheduledAt() { return this.formGroup.get('scheduledAt'); }
  get viewerSegment() { return this.formGroup.get('viewerSegment'); }
  get isScreenRotated() { return this.formGroup.get('isScreenRotated'); }
  get isViewerCountEnabled() { return this.formGroup.get('isViewerCountEnabled') as FormArray; }
  get broadcastBy() { return this.formGroup.get('broadcastBy'); }
  get isArchived() { return this.formGroup.get('isArchived'); }
  get archiveType() { return this.formGroup.get('archiveType'); }
  get archiveDays() { return this.formGroup.get('archiveDays'); }
  get thumbnailPath() { return this.formGroup.get('thumbnailPath'); }
  get thumbnailURL() { return this.formGroup.get('thumbnailURL'); }
  get creatorComment() { return this.formGroup.get('creatorComment'); }
  get isRadioMode() { return this.formGroup.get('isRadioMode'); }

  /**
   * リアクティブフォームを構築する
   * @return void
   */
  private createForm() {
    this.formGroup = this.fb.group({
      title: new FormControl('', [
        Validators.required,
        Validators.maxLength(40),
      ]),
      description: new FormControl('', Validators.maxLength(140)),
      broadcastType: new FormControl('', [
        Validators.required,
        Validators.pattern("0|1"),
      ]),
      scheduledAt: new FormControl('', [validateCurrentTime30PartAfterThanSmall(), validateTooManyValues()]),
      viewerSegment: new FormControl('', Validators.pattern("ALL|SUBSCRIBER|TICKET")),
      isScreenRotated: new FormControl(''),
      isViewerCountEnabled: new FormControl(''),
      isRadioMode: new FormControl(''),
      broadcastBy: new FormControl(''),
      isArchived: new FormControl(''),
      archiveType: new FormControl(''),
      archiveDays: new FormControl('', [Validators.min(1), Validators.required]),
      thumbnailPath: new FormControl(''),
      thumbnailURL: new FormControl(''),
      creatorComment: new FormControl('', Validators.maxLength(140)),
    });
  }

  /**
   * リアクティブフォームを設定する
   * @return void
   */
  private configureFormGroup(): void {
    // データ整形
    const scheduledAt: Date = this.live.scheduledAt !== null ? new Date(this.live.scheduledAt.seconds * 1000) : null;
    const archiveType = this.getArchiveType();

    // 編集フォームの初期値を設定
    this.formGroup.patchValue({
      title: this.live.title,
      description: this.live.description,
      broadcastType: this.live.scheduledAt === null ? BROADCAST_TYPE.immediate : BROADCAST_TYPE.scheduled,
      scheduledAt: scheduledAt === null ? null : scheduledAt.toLocaleDateString("ja-JP", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).replace(new RegExp('/', 'g'), '-') + 'T' + scheduledAt.toLocaleTimeString("ja-JP", {
        hour: "2-digit",
        minute: "2-digit"
      }),
      viewerSegment: this.live.options.viewerSegment,
      isViewerCountEnabled: this.live.options.isViewerCountEnabled,
      isRadioMode: this.live.options.isRadioMode,
      isScreenRotated: this.live.options.isScreenRotated,
      broadcastBy: this.live.options.broadcastBy,
      isArchived: this.live.options.isArchived,
      archiveType: archiveType,
      archiveDays: this.live.options.archiveDays,
      thumbnailPath: this.live.thumbnailPath,
      thumbnailURL: this.live.thumbnailURL
    });

    // マガジンIDに紐づくNGワードとグローバルNGワードをフロントバリデーションにセットする
    this.title.setValidators([containNgWordValidator(this.allNgWordList), Validators.required, Validators.maxLength(40)]);
    this.description.setValidators([containNgWordValidator(this.allNgWordList), Validators.maxLength(140)]);
    this.creatorComment.setValidators([containNgWordValidator(this.allNgWordList), Validators.maxLength(140)]);

    // 編集フォームの初期状態（非活性等）を設定
    const isDuringReservation = this.live.status === 'RESERVED' && this.live.scheduledAt !== null && this.room.status === 'booking' && this.room.kind === 'scheduled';
    this.isDuringReservation ? this.formGroup.controls.scheduledAt.enable() : this.formGroup.controls.scheduledAt.disable();
    if (this.live.status === 'CLOSED' || this.live.status === 'FORCE_STOPPED') {
      this.formGroup.controls.isArchived.enable()
      archiveType !== 'limited_time_release' ? this.formGroup.controls.archiveDays.disable() : this.formGroup.controls.archiveDays.enable();
      this.formGroup.controls.isRadioMode.disable();
    } else if (this.live.status === 'ON_AIR') {
      this.formGroup.controls.isRadioMode.disable();
      this.formGroup.controls.isArchived.disable();
      this.formGroup.controls.archiveDays.disable();
    } else {
      this.formGroup.controls.isArchived.disable();
      this.formGroup.controls.archiveDays.disable();
    }
  }

  private getArchiveType(): string {
    if (this.live.options.isArchived === null || this.live.options.isArchived === false) return "private";
    if (this.live.options.isArchived === true && this.live.options.archiveDays !== null && this.live.options.archiveDays !== 0) {
      // 公開期限日が過ぎた場合、非公開に設定する
      if (this.live.startedAt !== null) {
        const startedAtConvertedInMillisecond = this.live.startedAt.seconds * 1000;
        const ahciveDaysConvertedInMillisecond = this.live.options.archiveDays * 60 * 60 * 24 * 1000;
        const millsecondsAddedStartedAtToArchiveDays = startedAtConvertedInMillisecond + ahciveDaysConvertedInMillisecond

        if (new Date().getTime() - millsecondsAddedStartedAtToArchiveDays > 0) {
          return "private";
        }
      }
      return "limited_time_release"
    }
    return "public";
  }

  private getCreatorByUserId(userId: number): void {
    this.fsa.getUser(userId).subscribe( creator => {
      this.creator = creator;
    })
  }

  private getNgWord(magazineId?: number) {
    this.allNgWordList = [];
    MagliveApiClient.listGlobalNgWords()
      .subscribe(x => {
        if (x !== "NOT_AUTHORIZED") {
          x.map(ngWord => {
            this.allNgWordList.push(ngWord.word);
          })
        }
      })

    if (magazineId) {
      this.fsa.getNgwords(magazineId)
      .subscribe(x => {
        x.map(ngWord=> {
          let magazineNgWord = ngWord as MagazineNgWord;
          this.allNgWordList.push(magazineNgWord.word);
        })
      })
    }
  }

  private cancelEditLive(): void {
    this.router.navigate(['admin/lives'])
  }

  changeViewerCountCheckbox(event: any) {
    this.formGroup.patchValue({
      isViewerCountEnabled: !event.target.checked
    });
  }

  changeBroadcastByCheckbox(event: any) {
    this.formGroup.patchValue({
      broadcastBy: event.target.checked ? "Ext" : "App"
    });
  }

  changeArchiveType(value: string): void {
    switch (value) {
      case 'limited_time_release':
        this.formGroup.controls.archiveDays.enable();
        this.formGroup.patchValue({
          isArchived: true,
          archiveDays: this.live.options.archiveDays
        });
        break;
      case 'public':
        this.formGroup.controls.archiveDays.disable();
        this.formGroup.patchValue({
          isArchived: true,
          archiveDays: null
        });
        break;
      default:
        this.formGroup.controls.archiveDays.disable();
        this.formGroup.patchValue({
          isArchived: false,
          archiveDays: null
        });
    }
  }

  clickThumbnailButton(event: any): void {
    event.target.value = '';
  };

  changeThumbnail(event: any): void {
    const reader = new FileReader()
    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      this.selectedThumbnailFile = file;

      reader.onload = (() => {
        let image = new Image;
        image.src = reader.result as string;
        image.onload = () => {
          const factor = Math.abs(image.width / image.height - 16.0 / 9.0);
          const allowed = 0.015;
          this.invalidThumbnailSize = allowed <= factor;
        }
        this.formGroup.patchValue({
          thumbnailURL: reader.result
        });
      })
      reader.readAsDataURL(file);
    }
  }

  removeSelectingThumbnail(): void {
    if (this.thumbnailURL.value !== this.defaultThumbnailUrl) {
      this.selectedThumbnailFile = null;
      this.invalidThumbnailSize = false;
      this.formGroup.patchValue({
        thumbnailPath: null,
        thumbnailURL: this.defaultThumbnailUrl
      });
    }
  }

  public updateLive() {
    if (this.isWaiting) return;
    this.isWaiting = true;

    // サムネイル登録/削除処理
    let uploadRequest$ = Rx.of<"NOT_AUTHORIZED" | LiveThumbnail>({
      thumbnailPath: this.thumbnailPath.value,
      thumbnailURL: this.thumbnailURL.value
    })

    if (this.thumbnailURL.value !== this.live.thumbnailURL) {
      let shouldRegisterThumbnail = false;
      let shouldDeleteThumbnail = false;

      if ((!this.live.thumbnailURL || this.live.thumbnailURL === this.defaultThumbnailUrl) && this.thumbnailURL.value && this.thumbnailURL.value !== this.defaultThumbnailUrl) { // 未設定->登録
        shouldRegisterThumbnail = true;
      } else if ((this.live.thumbnailURL && this.live.thumbnailURL !== this.defaultThumbnailUrl) && (!this.thumbnailURL.value || this.thumbnailURL.value === this.defaultThumbnailUrl)) { // 登録済み->未設定
        shouldDeleteThumbnail = true;
      } else { // 登録済み->別のサムネイルに変更
        shouldDeleteThumbnail = true;
        shouldRegisterThumbnail = true;
      }

      if (shouldDeleteThumbnail) {
        uploadRequest$ = MagliveApiClient.deleteLiveThumbnail(this.live.magazineId, encodeURIComponent(this.live.thumbnailPath))
          .pipe(
            throttleTime(10000),
            map(() => {
              return {
                thumbnailPath: null,
                thumbnailURL: this.defaultThumbnailUrl
              }
            }),
            catchError(() => {
              this.toastr.error("サムネイル削除に失敗しました");
              setTimeout(() => { this.isWaiting = false; }, 3000);
              return Rx.EMPTY;
            })
          )
      }

      if (shouldRegisterThumbnail && this.selectedThumbnailFile) {
        const formData = new FormData();
        formData.append('file', this.selectedThumbnailFile);

        uploadRequest$ = MagliveApiClient.createLiveThumbnail(
          this.live.magazineId,
          formData
        ).pipe(
          throttleTime(10000),
          tap(response => {
            const thumbnailResult = response as LiveThumbnail;
            this.formGroup.patchValue({
              thumbnailPath: thumbnailResult.thumbnailPath,
              thumbnailURL: thumbnailResult.thumbnailURL
            });
          }),
          catchError(() => {
            this.toastr.error("サムネイル登録に失敗しました");
            setTimeout(() => { this.isWaiting = false; }, 3000);
            return Rx.EMPTY;
          })
        )
      }
    }

    uploadRequest$.pipe(
      concatMap(uploadRequest => {
        const thumbnailResult = uploadRequest as LiveThumbnail;

        // live更新処理
        // 送信する予約時刻(初期値に既存の値を設定)
        let convertedReservationTime: number | null = null;

        if (this.isDuringReservation) {
          // 予約時刻の変更は、予約配信のみに制限する
          if (this.scheduledAt.value !== null && this.live.scheduledAt !== null) {
            const convertExistingReservationTimeToDateType: Date = new Date(this.live.scheduledAt.seconds * 1000);
            // 既存の予約時刻の秒数を取得
            const existingReservationTimeSeconds = convertExistingReservationTimeToDateType.getSeconds();
            const convertRequestedReservationRimeToDateType = new Date(this.scheduledAt.value);
            // フォームで設定した予約時刻は秒数が省略されており、正確な時刻を設定できないため、既存の秒数を加算
            convertRequestedReservationRimeToDateType.setSeconds(convertRequestedReservationRimeToDateType.getSeconds() + existingReservationTimeSeconds);
            convertedReservationTime = Number(Date.parse(convertRequestedReservationRimeToDateType.toLocaleString()).toString().slice(0, -3));
          }
        }

        const params: LiveUpdateRequest = {
          userId: this.live.userId,
          magazineId: this.live.magazineId,
          title: this.title.value,
          description: this.description.value,
          thumbnailPath: thumbnailResult.thumbnailPath && thumbnailResult.thumbnailURL !== this.defaultThumbnailUrl ? thumbnailResult.thumbnailPath : null,
          scheduledAt: convertedReservationTime,
          options: {
            broadcastBy: this.broadcastBy.value,
            isScreenRotated: this.isScreenRotated.value,
            isViewerCountEnabled: this.isViewerCountEnabled.value,
            secondsViewWithoutAuthority: this.live.options.secondsViewWithoutAuthority,
            viewerSegment: this.viewerSegment.value,
            commentSegment: this.live.options.commentSegment,
            reactionSegment: this.live.options.reactionSegment,
            isArchived: this.isArchived.value,
            archiveDays: this.archiveDays.value,
            isRadioMode: this.isRadioMode.value
          },
        }

        console.log('[Debug] LiveUpdateRequest');
        console.table(params);

        return MagliveApiClient.updateLiveInfo(this.live.id, params).pipe(
          catchError(() => {
            this.toastr.error("ライブ更新に失敗しました");
            setTimeout(() => { this.isWaiting = false; }, 3000);
            return Rx.EMPTY;
          })
        )
      }),
      concatMap(() => {
        if (this.liveCreatorComment === this.creatorComment.value) return Rx.of([]);
        const params = {
          userId: this.live.userId,
          message: this.creatorComment.value,
          device: null
        }

        return MagliveApiClient.createCreatorComment(this.live.id, params).pipe(
          catchError(() => {
            this.toastr.error("固定コメント更新に失敗しました");
            setTimeout(() => { this.isWaiting = false; }, 3000);
            return Rx.EMPTY;
          })
        )
      }),
    ).subscribe(
      () => {
        setTimeout(() => { this.isWaiting = false; }, 3000);
        this.toastr.success("更新しました")
        this.router.navigate(['admin/lives'])
      },
      error => {
        setTimeout(() => { this.isWaiting = false; }, 3000);
        console.error(error)
      }
    )
  }

  // NOTE ライブ削除機能リリース時にコメント解除
  // deleteLive(): void {
  //   if(this.live.isDeleted) return;
  //   if (confirm(`削除してよろしいですか？削除後の変更はできません`)) {
  //     MagliveApiClient.deleteLive(this.live.id).subscribe(
  //       () => {
  //         this.toastr.success("ライブを削除しました");
  //         this.selectMenu('video_list');
  //       },
  //       error => {
  //         console.error(error)
  //         this.toastr.error("ライブ削除に失敗しました");
  //       }
  //     );
  //     return;
  //   }
  // }
}
